import { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { GenericOrder, PaymentIntent, PaymentResult } from '../../services/exports/Interfaces';
import { ERROR_CODES, PaymentResultErrorCode, PaymentStatus } from '../../services/exports/Constants';
import paymentLog from '../../services/helpers/PaymentLogger';
import PaymentManager from '../../services/api/PaymentManager';

export type { SpreedlyOffsiteNextAction } from '../../services/exports/Interfaces';

export interface SpreedlyQrSheetState {
  companyId: number;
  orderUuid: string;
  transactionToken: string;
  qrPayload: string | null;
  qrImageUrl: string | null;
  expiresAt: string | null;
  checkoutUrl: string | null;
}

export type BraintreeWallet = 'paypal' | 'venmo';

export interface SpreedlyBraintreeSheetState {
  orderUuid: string;
  transactionToken: string;
  environmentKey: string;
  method: BraintreeWallet;
}

const isBraintreeWallet = (method: string | null | undefined): method is BraintreeWallet =>
  method === 'paypal' || method === 'venmo';

export interface SpreedlyStripeApmSheetState {
  transactionToken: string;
  clientSecret: string;
  publishableKey: string;
  apmType: string | null;
}

const failed = (code?: PaymentResultErrorCode): PaymentResult => ({
  status: PaymentStatus.Failed,
  error: code ? { code } : null,
  result: null,
});

const pending: PaymentResult = { status: PaymentStatus.Pending, error: null, result: null };

/**
 * How long a redirect may go without the page starting to leave before it counts as refused. A
 * WebView that blocks the navigation raises nothing, and a started navigation fires pagehide or
 * beforeunload first — so a slow bank page never counts as refused, only one that never began.
 */
export const NAVIGATION_REFUSED_AFTER_MS = 5_000;

/** @returns A function that stops watching. */
function onNavigationRefused(callback: () => void): () => void {
  let left = false;
  const markLeft = () => {
    left = true;
  };

  const stopListening = () => {
    window.removeEventListener('beforeunload', markLeft);
    window.removeEventListener('pagehide', markLeft);
    document.removeEventListener('visibilitychange', markLeft);
  };

  window.addEventListener('beforeunload', markLeft, { once: true });
  window.addEventListener('pagehide', markLeft, { once: true });
  document.addEventListener('visibilitychange', markLeft, { once: true });

  const timer = setTimeout(() => {
    stopListening();

    if (!left && document.visibilityState === 'visible') {
      callback();
    }
  }, NAVIGATION_REFUSED_AFTER_MS);

  return () => {
    clearTimeout(timer);
    stopListening();
  };
}

/**
 * Only an https page is followed: the URL comes from the acquirer through our backend, and a
 * `javascript:` or plain-http one must never be navigated to.
 */
export function isSafeOffsiteUrl(url: string | null | undefined): url is string {
  try {
    return !!url && new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

interface Options {
  /**
   * Where to send the customer when the backend accepted the purchase but has nothing for them to
   * act on (Spreedly lost the gateway's answer): our own return URL for that order and
   * transaction, so the return handler and the order screen wait for the answer.
   */
  awaitingAnswerUrl?: (orderUuid: string, transactionToken: string) => string;
  /**
   * The redirect never started (a WebView refused it): the page is still here, so whatever waits on
   * the payment — the pay button's spinner — has to let go. Paying again resumes the same attempt.
   */
  onNavigationRefused?: () => void;
}

/**
 * Carries an offsite purchase (iDEAL, Bancontact, …) from the backend's 402 to the customer's bank
 * or wallet. Either way the page is left: a redirect goes straight to the checkout URL, and Stripe's
 * Payment Element redirects once the customer confirms in it. So a started payment resolves
 * `Pending`, and the order settles when the customer comes back (useHandleSpreedlyRedirectResult).
 *
 * The Payment Element needs a click inside our own UI first — the customer may have to pick a bank
 * or type a BLIK code — so that path opens a sheet and resolves only when the customer continues or
 * backs out.
 *
 * Pix leaves nothing either: the sheet shows the code the customer pays from their bank app and
 * polls the confirm endpoint until the acquirer reports back (useSpreedlyQrPolling).
 *
 * Braintree's PayPal and Venmo leave nothing: the wallet's own button opens in a popup, and the
 * nonce it hands back is confirmed with the backend right here, which answers paid or refused. So
 * that path resolves `Succeeded` or `Failed`, never `Pending`.
 */
export default function useSpreedlyOffsitePayment(companyId?: number, options: Options = {}) {
  const [sheet, setSheet] = useState<SpreedlyStripeApmSheetState | null>(null);
  const [braintreeSheet, setBraintreeSheet] = useState<SpreedlyBraintreeSheetState | null>(null);
  const [qrSheet, setQrSheet] = useState<SpreedlyQrSheetState | null>(null);
  const finishRef = useRef<((result: PaymentResult) => void) | null>(null);
  // The wallet sheet whose button already answered, so a second callback from the same sheet is
  // ignored. Keyed on the sheet object, not the transaction token: paying the same way again after a
  // cancel reopens the SAME purchase (the backend re-raises it) in a new sheet, and that one must be
  // confirmable.
  const submittedRef = useRef<SpreedlyBraintreeSheetState | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const stopWatchingNavigationRef = useRef<(() => void) | null>(null);

  // A sheet left open when the button unmounts (the customer navigated away) must not leave the
  // checkout awaiting it forever. Abandoned, not Cancelled: the page the sheet was on is gone,
  // so there is nobody to show "payment unsuccessful" to.
  useEffect(
    () => () => {
      finishRef.current?.(failed(PaymentResultErrorCode.Abandoned));
      finishRef.current = null;
      stopWatchingNavigationRef.current?.();
    },
    [],
  );

  const finish = useCallback((result: PaymentResult) => {
    finishRef.current?.(result);
    finishRef.current = null;
    setSheet(null);
    setBraintreeSheet(null);
    setQrSheet(null);
  }, []);

  const confirmPayment = useCallback(
    async (order: GenericOrder, paymentIntent: PaymentIntent): Promise<PaymentResult> => {
      const intent = paymentIntent;
      const offsite = intent?.offsite;
      const logAttrs = { order_uuid: order?.uuid, payment_method: offsite?.apm_type ?? 'offsite' };

      if (offsite?.type === 'redirect' && isSafeOffsiteUrl(offsite.checkout_url)) {
        paymentLog.info('spreedly', 'offsite_redirect', logAttrs);
        window.location.assign(offsite.checkout_url);
        stopWatchingNavigationRef.current = onNavigationRefused(() => {
          paymentLog.warn('spreedly', 'offsite_redirect_refused', logAttrs);
          optionsRef.current.onNavigationRefused?.();
        });

        return pending;
      }

      if (
        offsite?.type === 'stripe_payment_element' &&
        intent.token &&
        offsite.client_secret &&
        offsite.publishable_key
      ) {
        try {
          // Spreedly's element calls `window.Stripe` itself; loading here is what puts it there. It is
          // the release this package pins, not dev's v3: SpreedlyStripeApmSheet keeps v3's layout.
          await loadStripe(offsite.publishable_key);
        } catch (error) {
          paymentLog.error('spreedly', 'offsite_stripe_load_failed', {
            ...logAttrs,
            message: error instanceof Error ? error.message : undefined,
          });

          return failed();
        }

        paymentLog.info('spreedly', 'offsite_payment_element_opened', logAttrs);

        return new Promise<PaymentResult>((resolve) => {
          finishRef.current = resolve;
          setSheet({
            transactionToken: intent.token,
            clientSecret: offsite.client_secret,
            publishableKey: offsite.publishable_key,
            apmType: offsite.apm_type ?? null,
          });
        });
      }

      if (
        offsite?.type === 'braintree_buttons' &&
        offsite.transaction_token &&
        offsite.environment_key &&
        isBraintreeWallet(offsite.method)
      ) {
        paymentLog.info('spreedly', 'offsite_wallet_buttons_opened', { ...logAttrs, payment_method: offsite.method });

        return new Promise<PaymentResult>((resolve) => {
          finishRef.current = resolve;
          setBraintreeSheet({
            orderUuid: order?.uuid,
            transactionToken: offsite.transaction_token,
            environmentKey: offsite.environment_key,
            method: offsite.method as BraintreeWallet,
          });
        });
      }

      // The image is shown as an <img>: an https URL or an inline PNG from the acquirer, nothing else.
      const rawQrImageUrl = offsite?.qr_image_url ?? null;
      const qrImageUrl =
        rawQrImageUrl !== null && (rawQrImageUrl.startsWith('data:image/') || isSafeOffsiteUrl(rawQrImageUrl))
          ? rawQrImageUrl
          : null;

      if (offsite?.type === 'qr' && intent.token && (offsite.qr_payload || qrImageUrl)) {
        paymentLog.info('spreedly', 'offsite_qr_opened', logAttrs);

        return new Promise<PaymentResult>((resolve) => {
          finishRef.current = resolve;
          setQrSheet({
            companyId,
            orderUuid: order?.uuid,
            transactionToken: intent.token,
            qrPayload: offsite.qr_payload ?? null,
            qrImageUrl,
            expiresAt: offsite.expires_at ?? null,
            checkoutUrl: isSafeOffsiteUrl(offsite.checkout_url) ? offsite.checkout_url : null,
          });
        });
      }

      const awaitingUrl =
        !offsite && intent?.token && order?.uuid
          ? optionsRef.current.awaitingAnswerUrl?.(order.uuid, intent.token)
          : undefined;

      if (awaitingUrl) {
        // Accepted, but the gateway's answer is not in yet and may never come by itself (Spreedly
        // lost it mid-charge): not a failure — the money may have moved — so the customer waits on
        // the order screen, as after a bank redirect.
        paymentLog.info('spreedly', 'offsite_awaiting_answer', { ...logAttrs, state: intent.state });
        window.location.assign(awaitingUrl);
        stopWatchingNavigationRef.current = onNavigationRefused(() => {
          paymentLog.warn('spreedly', 'offsite_redirect_refused', logAttrs);
          optionsRef.current.onNavigationRefused?.();
        });

        return pending;
      }

      paymentLog.issue('spreedly', 'offsite_next_action_missing', { ...logAttrs, type: offsite?.type });

      return failed();
    },
    [companyId],
  );

  /**
   * The customer backed out of the sheet: a decision, not an error. A wallet sheet is reported to
   * the backend like a cancelled button, whether it was closed by Cancel, Escape or after the
   * approval timed out, so every way out of the sheet leaves the same trail.
   */
  const cancel = useCallback(() => {
    const current = braintreeSheet;

    if (current && submittedRef.current !== current) {
      submittedRef.current = current;

      paymentLog.info('spreedly', 'offsite_wallet_cancelled', {
        order_uuid: current.orderUuid,
        payment_method: current.method,
        source: 'sheet',
      });

      // Best effort, as for a cancelled button.
      void PaymentManager.submitSpreedlyOffsiteConfirm(
        {
          company_id: companyId,
          transaction_token: current.transactionToken,
          order_uuid: current.orderUuid,
          state: 'Cancelled',
          payment_method_type: current.method,
        },
        { [ERROR_CODES.CARD_EXCEPTION]: () => null },
      ).catch(() => undefined);
    }

    finish(failed(PaymentResultErrorCode.Cancelled));
  }, [braintreeSheet, companyId, finish]);

  /**
   * Stripe accepted the confirmation and is taking the customer to their bank. Watched like our
   * own redirect: a WebView that refuses Stripe's navigation leaves the page here, and the pay
   * button must let go rather than spin on a departure that never happens.
   */
  const redirecting = useCallback(() => {
    finish(pending);
    stopWatchingNavigationRef.current = onNavigationRefused(() => {
      paymentLog.warn('spreedly', 'offsite_redirect_refused', { payment_method: 'stripe_payment_element' });
      optionsRef.current.onNavigationRefused?.();
    });
  }, [finish]);

  /**
   * What the Braintree button reported, confirmed with the backend once per sheet: the bundle can
   * call back twice (a double click, a popup closing late), and a second confirm of the same
   * purchase would race the first. A new sheet for the same purchase — the customer cancelled and
   * paid the same way again — is confirmed afresh.
   *
   * A cancelled or failed button is reported too, so the backend settles the attempt at once
   * instead of it sitting `processing` until the sweep; the customer then pays again afresh.
   */
  const confirmWallet = useCallback(
    async (result: Spreedly.BraintreeCallbackResult): Promise<void> => {
      const current = braintreeSheet;

      if (!current || submittedRef.current === current) {
        return;
      }

      submittedRef.current = current;

      const logAttrs = { order_uuid: current.orderUuid, payment_method: current.method };
      const paymentMethodType = result.payment_method?.payment_method_type ?? current.method;

      if (result.state !== 'Successful' || !result.nonce) {
        const cancelled = result.state === 'Cancelled';

        paymentLog.info('spreedly', cancelled ? 'offsite_wallet_cancelled' : 'offsite_wallet_failed', {
          ...logAttrs,
          message: typeof result.message === 'string' ? result.message : undefined,
        });

        // Best effort: the answer changes nothing for the customer, who is released either way.
        void PaymentManager.submitSpreedlyOffsiteConfirm(
          {
            company_id: companyId,
            transaction_token: current.transactionToken,
            order_uuid: current.orderUuid,
            state: cancelled ? 'Cancelled' : 'Failed',
            payment_method_type: paymentMethodType,
          },
          { [ERROR_CODES.CARD_EXCEPTION]: () => null },
        ).catch(() => undefined);

        finish(failed(cancelled ? PaymentResultErrorCode.Cancelled : PaymentResultErrorCode.Declined));

        return;
      }

      const { success, status } = await PaymentManager.submitSpreedlyOffsiteConfirm(
        {
          company_id: companyId,
          transaction_token: current.transactionToken,
          order_uuid: current.orderUuid,
          state: result.state,
          payment_method_type: paymentMethodType,
          nonce: result.nonce,
          device_data: result.device_data ?? null,
          username: result.username ?? null,
        },
        // Keep the global error modal's "Continue" a plain dismiss, as the 3DS confirm does.
        { [ERROR_CODES.CARD_EXCEPTION]: () => null },
      );

      if (success) {
        paymentLog.info('spreedly', 'offsite_wallet_paid', logAttrs);
        finish({ status: PaymentStatus.Succeeded, error: null, result: null });

        return;
      }

      // 409 is the wallet refusing: the customer's business, not an application fault.
      if (status === 409) {
        paymentLog.info('spreedly', 'offsite_wallet_declined', logAttrs);
        finish(failed(PaymentResultErrorCode.Declined));

        return;
      }

      paymentLog.error('spreedly', 'offsite_wallet_confirm_failed', { ...logAttrs, status });
      finish(failed());
    },
    [braintreeSheet, companyId, finish],
  );

  /** The Pix sheet saw the payment settle (or expire). */
  const settleQr = useCallback((result: PaymentResult) => finish(result), [finish]);

  return { confirmPayment, sheet, cancel, redirecting, braintreeSheet, confirmWallet, qrSheet, settleQr };
}

/**
 * The return URL an offsite purchase comes back to. `offsite=1` tells the return handler to
 * confirm, and the order uuid rides along because on the web it is otherwise only in the path —
 * the backend expands the placeholder. Spreedly appends `transaction_token` itself.
 */
export const OFFSITE_RETURN_PARAMS = ['offsite', 'transaction_token', 'payment_gateway'] as const;

/**
 * The query the storefront carries on after a paid return, without the return's own parameters:
 * the transaction token is a bearer credential with no further use, and `offsite=1` would make
 * a later visit to the order look like another return.
 */
export function withoutOffsiteReturnParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([key]) => !(OFFSITE_RETURN_PARAMS as readonly string[]).includes(key)),
  ) as Partial<T>;
}

export function withOffsiteReturnParams(returnUrl: string, orderUuidPlaceholder: string): string {
  // Appended as text, not through URL/URLSearchParams: those percent-encode the placeholders
  // (braces and colons) already in the path and query, and the backend's literal replace would
  // then miss them.
  const separator = returnUrl.includes('?') ? '&' : '?';
  const uuid = /[?&]uuid=/.test(returnUrl) ? '' : `&uuid=${orderUuidPlaceholder}`;

  return `${returnUrl}${separator}offsite=1${uuid}`;
}
